import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosGet, axiosPatch, axiosPost } from 'Redux/axiosHelper'
import {
  AdminResp,
  CreateAdminPayload,
  CreateBlockResp,
  FindAdminByEmail,
} from 'types/settings/general'

const getAllAdmins = async (params: { isBlocked: boolean }) => {
  const response = await axiosGet(`v1/admins/get-all-admins`, params)
  return response.data
}

export const useGetAllAdmins = (params: { isBlocked: boolean }) => {
  const admins = useQuery<AdminResp[]>(
    ['get-all-admins'],
    () => getAllAdmins(params),
    {
      enabled: false,
    },
  )
  return admins
}

// reactivate admin account
const reactivateAdminAccount = async (payload: { email: string }) => {
  const response = await axiosPatch(payload, `v1/users/reactivate`)
  return response.data
}

export const useReactivateAdminAccount = () => {
  const queryClient = useQueryClient()
  return useMutation(reactivateAdminAccount, {
    onSuccess: (updatedAdmin: CreateBlockResp) => {
      queryClient.setQueryData(
        ['get-all-admins'],
        (oldData: AdminResp[] | undefined) => {
          if (!oldData) return [updatedAdmin]
          return oldData.filter(admin => admin._id !== updatedAdmin.admin._id)
        },
      )
    },
  })
}

// deactivate admin account
const deactivateAdminAccount = async (payload: { email: string }) => {
  const response = await axiosPatch(payload, `v1/users/block-email`)
  return response.data
}

export const useDeactivateAdminAccount = () => {
  const queryClient = useQueryClient()
  return useMutation(deactivateAdminAccount, {
    onSuccess: (updatedAdmin: CreateBlockResp) => {
      queryClient.setQueryData(
        ['get-all-admins'],
        (oldData: AdminResp[] | undefined) => {
          if (!oldData) return [updatedAdmin]
          return oldData.filter(admin => admin._id !== updatedAdmin.admin._id)
        },
      )
    },
  })
}

// change password
const changePassword = async (payload: {
  password: string
  firebaseId: string
}) => {
  const response = await axiosPost(
    payload,
    `v1/users/set-new-password`,
    'Password updated successfully!',
  )
  return response.data
}

export const useChangePassword = () => {
  return useMutation(changePassword)
}

// find admin by email
const findAdminByEmail = async (params: { email: string }) => {
  const response = await axiosGet(`v1/admins/search`, params)
  return response.data
}
export const useFindAdminByEmail = (params: { email: string }) => {
  const adminByEmail = useQuery<FindAdminByEmail>(
    ['get-admin-by-email', params],
    () => (params.email ? findAdminByEmail(params) : null),
  )
  return adminByEmail
}

// create admin
const createAdmin = async (payload: CreateAdminPayload) => {
  const response = await axiosPost(payload, `v1/admins/create`)
  return response.data
}
export const useCreateAdmin = () => {
  const queryClient = useQueryClient()
  return useMutation(createAdmin, {
    onSuccess: (newAdmin: AdminResp) => {
      queryClient.setQueryData(
        ['get-all-admins'],
        (oldData: AdminResp[] | undefined) => {
          if (!oldData) return [newAdmin]
          return [...oldData, newAdmin]
        },
      )
    },
  })
}

// get admin details
const getAdminDetailsById = async (
  id: string,
  params: { sendSessions: boolean },
) => {
  const response = await axiosGet(`v1/admins/${id}`, params)
  return response.data
}
export const useGetAdminDetailsById = (
  id: string,
  params: { sendSessions: boolean },
) => {
  const adminDetails = useQuery<AdminResp>(
    ['get-admin-details-by-id', id],
    () => getAdminDetailsById(id, params),
    { enabled: false },
  )
  return adminDetails
}
